<template>
  <div class="min-h-screen bg-white">
    <div class="px-5 sm:px-6 lg:px-10 py-8 max-w-[80%] mx-auto ">
      <!-- Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-3">
          <img
            :src="logoSrc"
            alt="LYON"
            class="h-10 w-auto object-contain"
            @error="onLogoError"
          />
        </div>

        <div class="text-sm text-gray-700">
          {{ companyName || 'company name' }}
        </div>
      </div>

      <!-- Title + PDF -->
      <div class="mt-8 mb-12">
        <div class="flex items-center justify-center relative">
          <h2 class="text-center text-gray-900 font-semibold">
            قائمة الدخل عن الفترة المنتهية في {{ titleDate }}
          </h2>

          <div class="absolute right-0">
            <button
              type="button"
              class="px-4 py-2 rounded-full text-white text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled
              title="Backend not ready"
              @click="generatePdf"
            >
              Generate PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Accordion list -->
      <div class="mt-8 space-y-3" dir="">
        <div
          v-for="sec in sections"
          :key="sec.key"
          class="rounded-xl border border-gray-200 overflow-hidden bg-white"
        >
          <!-- Row header -->
          <button
            type="button"
            class="w-full flex items-center justify-between px-4 py-3"
            :class="[
              sec.expandable ? 'cursor-pointer' : 'cursor-default opacity-90'
            ]"
            :disabled="!sec.expandable"
            @click="sec.expandable ? toggle(sec.key) : null"
          >
            <div class="flex items-center gap-3">
              <!-- ✅ hide +/- for non-expandable sections -->
              <span
                v-if="sec.expandable"
                class="inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 text-gray-600 text-xs leading-none"
              >
                {{ isOpen(sec.key) ? '−' : '+' }}
              </span>

              <span class="text-sm text-gray-700">{{ sec.title }}</span>
            </div>

            <div class="flex items-center gap-2 text-xs text-gray-500">
              <span>Total Price:</span>
              <span class="font-semibold text-gray-900">{{ format3(sec.total) }}</span>
            </div>
          </button>

          <!-- Details -->
          <div v-if="sec.expandable && isOpen(sec.key) && sec.details.length" class="border-t border-gray-200">
            <div class="divide-y divide-gray-200">
              <div
                v-for="d in sec.details"
                :key="d.code + '-' + d.label"
                class="px-4 py-3 flex items-center justify-between"
              >
                <div class="flex items-center gap-4 text-sm text-gray-700">
                  <span class="text-xs text-gray-500 w-10">{{ d.code }}</span>
                  <span>{{ d.label }}</span>
                </div>

                <div class="text-sm font-semibold text-gray-900">
                  {{ format3(d.amount) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="h-10"></div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

const companyId = computed(() => String(route.query.company_id ?? ''))
const from = computed(() => String(route.query.from ?? ''))
const to = computed(() => String(route.query.to ?? ''))

const companyName = ref('')

const logoSrc = ref('/assets/lyon-logo.png')
function onLogoError() {
  logoSrc.value =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="40"><text x="0" y="28" font-size="22" fill="#0b5ea8">LYON</text></svg>`
    )
}

async function loadCompanies() {
  return [
    { label: 'Lyon Group', value: 1 },
    { label: 'Global Gate', value: 2 },
    { label: 'Codexal', value: 3 },
  ]
}

watchEffect(async () => {
  const list = await loadCompanies()
  const found = list.find((c) => String(c.value) === String(companyId.value))
  companyName.value = found?.label ?? ''
})

const titleDate = computed(() => {
  // نفس الصورة: تاريخ واحد بالعنوان
  if (to.value) return to.value.split('-').reverse().join('-')
  if (from.value) return from.value.split('-').reverse().join('-')
  return '30-09-2025'
})

const baseSections = ref([
  {
    key: 'revenues',
    title: 'الإيرادات',
    details: [
      { code: '', label: 'تفاصيل الإيرادات', amount: 0 },
      { code: '411', label: 'الإيرادات الخاضعة', amount: 0 },
      { code: '412', label: 'الإيرادات المعفاة', amount: 0 },
      { code: '413', label: 'الإيرادات الخاضعة بنسبة صفر', amount: 0 },
      { code: '414', label: 'الإيرادات الأخرى', amount: 0 },
      { code: '415', label: 'إيرادات العقود', amount: 0 },
    ],
  },
  {
    key: 'cost_revenues',
    title: 'تكلفة الإيرادات',
    details: [
      { code: '421', label: 'تأمين سيارات', amount: 0 },
      { code: '422', label: 'تأمين سيارات', amount: 0 },
      { code: '424', label: 'كوشوك سيارات', amount: 0 },
      { code: '425', label: 'صيانة سيارات', amount: 0 },
      { code: '426', label: 'قطع سيارات', amount: 0 },
      { code: '427', label: 'دهان سيارات', amount: 0 },
      { code: '429', label: 'مواقف', amount: 0 },
      { code: '430', label: 'غيار زيت', amount: 0 },
      { code: '431', label: 'بنشر سيارات', amount: 0 },
      { code: '432', label: 'غسيل سيارات', amount: 0 },
      { code: '433', label: 'مخالفات', amount: 0 },
      { code: '434', label: 'تتبع السيارات', amount: 0 },
      { code: '435', label: 'ترخيص هيئة تنظيم النقل', amount: 0 },
      { code: '436', label: 'زيوت', amount: 0 },
    ],
  },
  { key: 'gross_profit', title: 'إجمالي الربح', details: [] },
  {
    key: 'admin_general',
    title: 'مصاريف ادارية وعمومية',
    details: [
      { code: '611', label: 'م.كهرباء', amount: 0 },
      { code: '612', label: 'م.الإيجار', amount: 0 },
      { code: '613', label: 'م.مياه', amount: 0 },
      { code: '614', label: 'م.إنترنت', amount: 0 },
      { code: '615', label: 'م.متفرقة', amount: 0 },
      { code: '616', label: 'غرامات الضمان الاجتماعي', amount: 0 },
      { code: '617', label: 'غرامات ضريبة الدخل والمبيعات', amount: 0 },
      { code: '618', label: 'م.ضيافة', amount: 0 },
      { code: '619', label: 'م.نظافة', amount: 0 },
      { code: '620', label: 'م.قرطاسية', amount: 0 },
      { code: '621', label: 'م.عمولات قروض', amount: 0 },
      { code: '622', label: 'م.الدعاية والإعلان', amount: 0 },
      { code: '623', label: 'م.عمولات بنكية', amount: 0 },
      { code: '624', label: 'م.أتعاب محاماة', amount: 0 },
      { code: '625', label: 'أتعاب التدقيق', amount: 0 },
      { code: '626', label: 'رسوم حكومية وتراخيص', amount: 0 },
      { code: '627', label: 'م.هدايات بنكية', amount: 0 },
      { code: '628', label: 'م.اشتراكات', amount: 0 },
      { code: '629', label: 'م.سفر وإقامة', amount: 0 },
      { code: '631', label: 'م.صيانة أجهزة', amount: 0 },
      { code: '632', label: 'م.هواتف', amount: 0 },
      { code: '633', label: 'م.كمبيوتر وطابعات', amount: 0 },
      { code: '634', label: 'م.قرطاسية', amount: 0 },
      { code: '635', label: 'م.عمولات فيزا', amount: 0 },
    ],
  },
  {
    key: 'salaries_wages',
    title: 'رواتب و اجور',
    details: [
      { code: '0000', label: 'م.الرواتب', amount: 0 },
      { code: '0000', label: 'م.مكافآت', amount: 0 },
      { code: '0000', label: 'م.ضمان اجتماعي', amount: 0 },
      { code: '0000', label: 'م.فحوصات', amount: 0 },
    ],
  },
  { key: 'net_income', title: 'الدخل الصافي', details: [] },
])

const NON_EXPANDABLE_KEYS = new Set(['gross_profit', 'net_income'])

const openSet = ref(new Set())

function isOpen(key) {
  return openSet.value.has(key)
}

function toggle(key) {
  if (NON_EXPANDABLE_KEYS.has(key)) return
  const next = new Set(openSet.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  openSet.value = next
}

const sections = computed(() => {
  return baseSections.value.map((s) => {
    const total = (s.details || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
    const expandable = !NON_EXPANDABLE_KEYS.has(s.key) && Array.isArray(s.details) && s.details.length > 0
    return { ...s, total, expandable }
  })
})

function format3(n) {
  const s = Math.round(Number(n || 0)).toString()
  return s.padStart(3, '0')
}

function generatePdf() {
  // backend not ready
}
</script>
